
import Vue from "vue"
import Component from "vue-class-component"
import {Inject} from "vue-property-decorator"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {AuthenticationService} from "@/AuthenticationService"
import CloseButton from "@/components/CloseButton.vue"
import Message from "@/components/Message.vue"
import DetailGrid, {DetailRow, DetailRowAction} from "@/components/DetailGrid.vue"
import {
  DASH,
  formatDate,
  formatDateTime,
  formatDollars,
  formatDollarsSum,
  formatPercentValue,
  formatText,
  isBlank,
} from "@/utils/formatter"
import {
  ACCOUNT_STATUS_OPTIONS,
  CLASSIFICATION_OPTIONS,
  CONTRIBUTION_STATUS_OPTIONS,
  MFA_TYPE_OPTIONS,
  optionLabel,
} from "@/superannuation/memberLookups"
import {
  ACCOUNT_ACTIONS,
  AccountAction,
  AccountAuditEntry,
  accountAuditUrl,
  accountEmailUrl,
  AccountRowId,
  actionDisabledReason,
  hasEmail,
  isValidEmailInput,
  MemberAccountState,
} from "@/superannuation/memberAccountActions"

// full read-only detail returned by GET /api/members/summary/{id} - field
// names match au.com.ajg.ssn.model.MemberSummaryDetailDto exactly
interface MemberDetail extends MemberAccountState {
  id: string
  number: string
  username: string
  title: string | null
  firstnameSurname: string
  gender: string | null
  dateOfBirth: string | null
  occupation: string | null
  beneficiaryNomination: string | null
  effectiveEmail: string | null
  mobile: string | null
  postcode: string | null
  companyName: string | null

  grossSalary: number | null
  additionalSalary: number | null
  totalSalary: number | null
  contributionStatus: string | null
  actualEmployerSuperContributions: number | null
  employeeSuperContributions: number | null
  employeeSuperNonConcessionalContributions: number | null

  superBalance: number | null
  taxedBalance: number | null
  untaxedBalance: number | null
  additionalSuperBalance: number | null
  totalTaxedBalance: number | null
  totalSuperBalance: number | null
  growthExposure: number | null
  projectedIncome: number | null
  projectedBalance: number | null

  insuranceCategory: string | null
  deathCover: number | null
  tpdCover: number | null
  monthlyDeathTpdPremium: number | null
  deathPremium: number | null
  tpdPremium: number | null
  salaryContinuationCover: number | null
  monthlySalaryContinuationPremium: number | null

  ttrCommencementDate: string | null
  ttrBalance: number | null
  ttrDrawdown: number | null
  taxedBenefitComponent: number | null
  untaxedBenefitComponent: number | null

  firstLoginDate: string | null
  lastLoginDate: string | null
  contactRequested: string | null
  emailOpened: string | null
  reportDownloaded: string | null
  adviceDownloaded: string | null
  projectionModelled: string | null
  contributionCalculated: string | null
  riskProfileAssessed: string | null
  insuranceAssessed: string | null
  beneficiaryAssessed: string | null
  ttrCalculated: string | null

  accountStatus: string | null
  superannuationPlatformJoinDate: string | null
  companyJoinDate: string | null
  dateCreated: string | null
  dateUpdated: string | null
  dataDate: string | null
  updatedBy: string | null

  passwordExpiryDate: string | null
  mustChangePassword: boolean
  activationAttempts: number | null
  failedAuthenticationAttempts: number
  mfaType: string | null
}
interface DetailSection {
  title: string
  rows: Array<DetailRow>
}

const INITIALLY_COLLAPSED_SECTIONS = ["TTR", "Activity", "Insurance", "Salary and Contributions"]
const collapseAll = (acc: Record<string, boolean>, title: string): Record<string, boolean> => {
  acc[title] = true
  return acc
}

const LONG_MEMBER_NUMBER_LENGTH = 32

function formatMemberNumber(number: string | null): string {
  return isBlank(number) ? DASH : number.toUpperCase()
}

// read-only "Member Detail" popup, opened via open(memberId) from MembersView
// (e.g. on click of a member's number in the members table)
@Component({components: {CloseButton, Message, DetailGrid}})
export default class MemberDetailDialog extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  visible = false
  memberDetail: MemberDetail | null = null
  memberDetailInProgress = false
  memberDetailError: string | null = null

  // adviser/staff/admin may trigger account operations, employers only the
  // welcome email
  canManageAccount = false
  isEmployer = false
  isAdmin = false

  // account action confirmation / result
  pending: AccountAction | null = null
  confirmVisible = false
  confirmText = ""
  sendEmail = false
  running = false
  deleted = false
  resultMessage: string | null = null
  resultError = false

  // email (override) editing, in a small popup like the action confirmations
  emailDialogVisible = false
  // null when cleared via the input's clear button
  emailInput: string | null = ""
  savingEmail = false

  // account history (audit trail), loaded when first expanded
  audit: Array<AccountAuditEntry> = []
  auditCollapsed = true
  auditLoading = false
  auditError: string | null = null

  collapsedSections: Record<string, boolean> = INITIALLY_COLLAPSED_SECTIONS.reduce<
    Record<string, boolean>
  >(collapseAll, {})

  async open(memberId: string) {
    this.visible = true
    this.memberDetail = null
    this.memberDetailError = null
    this.memberDetailInProgress = true
    this.deleted = false
    this.resultMessage = null
    this.emailDialogVisible = false
    this.audit = []
    this.auditCollapsed = true
    this.auditError = null
    this.collapsedSections = INITIALLY_COLLAPSED_SECTIONS.reduce<Record<string, boolean>>(
      collapseAll,
      {}
    )
    this.canManageAccount = await this.authenticationService.isStaff()
    this.isEmployer = await this.authenticationService.isEmployer()
    this.isAdmin = await this.authenticationService.isAdmin()
    await this.load(memberId)
  }

  private async load(memberId: string) {
    await Axios.get(`/api/members/summary/${memberId}`)
      .then((response: AxiosResponse) => (this.memberDetail = response.data))
      .catch((reason: AxiosError<any>) => {
        this.memberDetailError = reason.response?.data.message ?? "Failed to load member detail"
      })
      .finally(() => (this.memberDetailInProgress = false))
  }

  // hidden (rather than disabled) when the action can't currently be used
  private rowActions(rowId: AccountRowId): Array<DetailRowAction> {
    const m = this.memberDetail
    if (!m || this.deleted) return []
    return ACCOUNT_ACTIONS.filter(
      a =>
        a.rowId === rowId &&
        actionDisabledReason(a, m, this.canManageAccount, this.isEmployer, this.isAdmin) === null
    ).map(a => ({key: a.key, label: a.button, tooltip: a.label}))
  }

  private emailActions(): Array<DetailRowAction> {
    const actions = this.rowActions("email")
    if (
      this.canManageAccount &&
      !this.deleted &&
      this.memberDetail?.accountActionsBlocker == null
    ) {
      actions.push({
        key: "editEmail",
        label: "EDIT",
        tooltip: "Edit email",
      })
    }

    return actions
  }

  private onRowAction(key: string) {
    if (key === "editEmail") {
      this.emailInput = this.memberDetail?.effectiveEmail ?? ""
      this.emailDialogVisible = true
      return
    }
    this.pending = ACCOUNT_ACTIONS.find(a => a.key === key) ?? null
    this.sendEmail = false
    this.confirmText = ""
    this.confirmVisible = this.pending !== null
  }

  // destructive actions need the username typed in to confirm
  get confirmed(): boolean {
    return (
      this.pending?.removesMember !== true ||
      this.confirmText.trim() === this.memberDetail?.username
    )
  }

  get canSendEmail(): boolean {
    return this.memberDetail !== null && hasEmail(this.memberDetail)
  }

  get sendEmailLabel(): string {
    return this.canSendEmail
      ? `Also email the new code to ${this.memberDetail?.effectiveEmail ?? ""}`
      : "Also email the new code (member has no email address)"
  }

  get emailValid(): boolean {
    return isValidEmailInput(this.emailInput)
  }

  get emailChanged(): boolean {
    return (this.emailInput ?? "").trim() !== (this.memberDetail?.effectiveEmail ?? "")
  }

  private failure(prefix: string, reason: AxiosError<any>) {
    this.resultError = true
    const detail: string =
      reason.response?.data?.message ?? reason.response?.statusText ?? "Unexpected error"
    this.resultMessage = `${prefix} failed: ${detail}`
  }

  async saveEmail() {
    if (!this.memberDetail || this.savingEmail || !this.emailValid || !this.emailChanged) return
    this.savingEmail = true
    this.resultMessage = null
    const id = this.memberDetail.id
    await Axios.post(accountEmailUrl(id), {email: (this.emailInput ?? "").trim()})
      .then(async () => {
        this.resultError = false
        this.resultMessage = "Email updated"
        await this.load(id)
        await this.refreshAudit()
      })
      .catch((reason: AxiosError<any>) => this.failure("Email update", reason))
      .finally(() => {
        this.savingEmail = false
        this.emailDialogVisible = false
      })
  }

  async run() {
    const action = this.pending
    const id = this.memberDetail?.id
    if (!action || id === undefined || this.running) return
    this.running = true
    this.resultMessage = null
    const parameters =
      action.params ?? (action.supportsSendEmail === true ? {sendEmail: this.sendEmail} : undefined)
    await Axios.request({method: action.method ?? "post", url: action.url(id), params: parameters})
      .then(async () => {
        this.resultError = false
        this.resultMessage = action.success
        if (action.removesMember === true) this.deleted = true
        else {
          await this.load(id)
          await this.refreshAudit()
        }
      })
      .catch((reason: AxiosError<any>) => this.failure(action.label, reason))
      .finally(() => {
        this.running = false
        this.confirmVisible = false
      })
  }

  private async loadAudit() {
    const id = this.memberDetail?.id
    if (id === undefined) return
    this.auditLoading = true
    this.auditError = null
    await Axios.get(accountAuditUrl(id))
      .then((response: AxiosResponse) => (this.audit = response.data))
      .catch((reason: AxiosError<any>) => {
        this.auditError = reason.response?.data?.message ?? "Failed to load account history"
      })
      .finally(() => (this.auditLoading = false))
  }

  private async refreshAudit() {
    if (!this.auditCollapsed) await this.loadAudit()
  }

  private async toggleAudit() {
    this.auditCollapsed = !this.auditCollapsed
    if (!this.auditCollapsed) await this.loadAudit()
  }

  private formatAuditTime(value: string): string {
    return formatDateTime(value)
  }

  private toggleSection(title: string) {
    this.$set(this.collapsedSections, title, !this.collapsedSections[title])
  }

  // primary bio fields shown as a compact header row above the grouped
  // sections below
  get memberDetailOverview(): Array<DetailRow> {
    if (!this.memberDetail) return []
    const m = this.memberDetail
    return [
      {
        id: "member",
        actions: this.rowActions("member"),
        label: "Member #",
        value: formatMemberNumber(m.number),
        small: !isBlank(m.number) && m.number.length === LONG_MEMBER_NUMBER_LENGTH,
        mono: true,
      },
      {label: "Username", value: formatText(m.username)},
      {label: "Title", value: formatText(m.title)},
      {label: "Name", value: formatText(m.firstnameSurname)},
      {label: "Gender", value: formatText(m.gender)},
      {label: "Date of Birth", value: formatDate(m.dateOfBirth)},
      {label: "Employer", value: formatText(m.companyName)},
      {label: "Occupation", value: optionLabel(CLASSIFICATION_OPTIONS, m.occupation)},
      {label: "Beneficiary Nomination", value: formatText(m.beneficiaryNomination)},
      {
        id: "email",
        actions: this.emailActions(),
        label: "Email",
        value: formatText(m.effectiveEmail),
      },
      {label: "Mobile", value: formatText(m.mobile)},
      {label: "Postcode", value: formatText(m.postcode)},
    ]
  }

  // grouped sections rendered below the overview, in display order
  get memberDetailSections(): Array<DetailSection> {
    if (!this.memberDetail) return []
    const m = this.memberDetail
    return [
      {
        title: "Salary and Contributions",
        rows: [
          {label: "Salary", value: formatDollars(m.grossSalary)},
          {label: "Additional Salary", value: formatDollars(m.additionalSalary)},
          {label: "Total Salary", value: formatDollars(m.totalSalary)},
          {label: "Projected Income", value: formatDollars(m.projectedIncome)},
          {
            label: "Contribution Status",
            value: optionLabel(CONTRIBUTION_STATUS_OPTIONS, m.contributionStatus),
          },
          {
            label: "Employer Contributions",
            value: formatDollars(m.actualEmployerSuperContributions),
          },
          {label: "Employee Contributions", value: formatDollars(m.employeeSuperContributions)},
          {
            label: "Non-concessional Contributions",
            value: formatDollars(m.employeeSuperNonConcessionalContributions),
          },
        ],
      },
      {
        title: "Super Balance",
        rows: [
          {label: "Balance", value: formatDollars(m.superBalance)},
          {label: "Taxed Balance", value: formatDollars(m.taxedBalance)},
          {label: "Untaxed Balance", value: formatDollars(m.untaxedBalance)},
          {label: "Growth Exposure", value: formatPercentValue(m.growthExposure)},
          {label: "Additional Balance", value: formatDollars(m.additionalSuperBalance)},
          {label: "Total Taxed Balance", value: formatDollars(m.totalTaxedBalance)},
          {label: "Total Balance", value: formatDollars(m.totalSuperBalance)},
          {label: "Projected Balance", value: formatDollars(m.projectedBalance)},
        ],
      },
      {
        title: "Insurance",
        rows: [
          {label: "Death Cover", value: formatDollars(m.deathCover)},
          {label: "TPD Cover", value: formatDollars(m.tpdCover)},
          {label: "IP Cover", value: formatDollars(m.salaryContinuationCover)},
          {label: "Category", value: formatText(m.insuranceCategory)},
          {label: "Death Premium", value: formatDollars(m.deathPremium)},
          {label: "TPD Premium", value: formatDollars(m.tpdPremium)},
          {label: "IP Premium", value: formatDollars(m.monthlySalaryContinuationPremium)},
          {
            label: "Total Premiums",
            value: formatDollarsSum(m.monthlyDeathTpdPremium, m.monthlySalaryContinuationPremium),
          },
        ],
      },
      {
        title: "TTR",
        rows: [
          {label: "Commencement Date", value: formatDate(m.ttrCommencementDate)},
          {label: "Balance", value: formatDollars(m.ttrBalance)},
          {label: "Drawdown", value: formatDollars(m.ttrDrawdown)},
          {label: "Projected Drawdown", value: DASH},
          {label: "Taxed Benefit Component", value: formatDollars(m.taxedBenefitComponent)},
          {label: "Untaxed Benefit Component", value: formatDollars(m.untaxedBenefitComponent)},
          {label: "Projected Tax Savings", value: DASH},
          {label: "Projected Balance", value: DASH},
        ],
      },
      {
        title: "Activity",
        rows: [
          {label: "First Login", value: formatDateTime(m.firstLoginDate)},
          {label: "Last Login", value: formatDateTime(m.lastLoginDate)},
          {label: "Contact Requested", value: formatDateTime(m.contactRequested)},
          {label: "Email Opened", value: formatDateTime(m.emailOpened)},
          {label: "Report Downloaded", value: formatDateTime(m.reportDownloaded)},
          {label: "Advice Downloaded", value: formatDateTime(m.adviceDownloaded)},
          {
            id: "projectionModelled",
            actions: this.rowActions("projectionModelled"),
            label: "Projection Modelled",
            value: formatDateTime(m.projectionModelled),
          },
          {
            id: "contributionCalculated",
            actions: this.rowActions("contributionCalculated"),
            label: "Contribution Modelled",
            value: formatDateTime(m.contributionCalculated),
          },
          {
            id: "riskProfileAssessed",
            actions: this.rowActions("riskProfileAssessed"),
            label: "Risk Profile Assessed",
            value: formatDateTime(m.riskProfileAssessed),
          },
          {
            id: "insuranceAssessed",
            actions: this.rowActions("insuranceAssessed"),
            label: "Insurance Assessed",
            value: formatDateTime(m.insuranceAssessed),
          },
          {label: "Beneficiary Assessed", value: formatDateTime(m.beneficiaryAssessed)},
          {
            id: "ttrCalculated",
            actions: this.rowActions("ttrCalculated"),
            label: "TTR Modelled",
            value: formatDateTime(m.ttrCalculated),
          },
        ],
      },
      {
        title: "Account",
        rows: [
          {
            id: "accountStatus",
            actions: this.rowActions("accountStatus"),
            label: "Account Status",
            value: optionLabel(ACCOUNT_STATUS_OPTIONS, m.accountStatus),
          },
          {label: "Created", value: formatDateTime(m.dateCreated)},
          {label: "Updated", value: formatDateTime(m.dateUpdated)},
          {label: "Updated By", value: formatText(m.updatedBy)},
          {label: "Platform Join Date", value: formatDate(m.superannuationPlatformJoinDate)},
          {label: "Employer Join Date", value: formatDate(m.companyJoinDate)},
          {label: "Data Date", value: formatDate(m.dataDate)},
          {
            id: "mfa",
            actions: this.rowActions("mfa"),
            label: "MFA",
            value: optionLabel(MFA_TYPE_OPTIONS, m.mfaType),
          },
          {
            id: "activations",
            actions: this.rowActions("activations"),
            label: "Activations",
            value: m.activationAttempts == null ? DASH : String(m.activationAttempts),
          },
          {
            id: "failedLogins",
            actions: this.rowActions("failedLogins"),
            label: "Failed Logins",
            value: String(m.failedAuthenticationAttempts),
          },
          {
            id: "passwordExpiry",
            actions: this.rowActions("passwordExpiry"),
            label: "Password Expiry",
            value: formatDate(m.passwordExpiryDate),
          },
          {
            id: "mustChangePassword",
            label: "Must Change Password",
            value: m.mustChangePassword ? "Yes" : "No",
          },
        ],
      },
    ]
  }
}
